import { t } from "@/i18n-js/instance";
import { formatPaywallCurrencyAmount } from "@/react/helpers/paywalls/currencies";
import type { CommunityMemberCharge } from "@/react/types/CommunityMemberCharge";
import { localeStringRoot } from "../constants";

interface UseRefundChargeFormProps {
  charge: CommunityMemberCharge;
}

interface UseRefundChargeFormReturn {
  maxAmountToRefund: (value: string) => boolean | string;
  minAmountToRefund: (value: string) => boolean | string;
  transformValueIntoCurrency: (value: string) => string | undefined;
}

export const useRefundChargeForm = ({
  charge,
}: UseRefundChargeFormProps): UseRefundChargeFormReturn => {
  const maximumAmountToRefund = charge.amount_to_refund_raw;
  const removeAllDigits = (value: string): string => value.replace(/\D/g, "");

  const transformValueIntoCurrency = (value: string): string | undefined => {
    const amountInCents = Number(removeAllDigits(value));

    if (!isNaN(amountInCents)) {
      return formatPaywallCurrencyAmount(charge.currency, amountInCents, {
        includeCurrencyCode: false,
        forceMaxFractionDigits: true,
      });
    }

    return undefined;
  };

  const minAmountToRefund = (value: string): boolean | string => {
    const amountInCents = Number(removeAllDigits(value));

    return (
      amountInCents >= 1 ||
      t(`${localeStringRoot}.refund_form.alerts.minimun_amount_required`, {
        value: formatPaywallCurrencyAmount(charge.currency, 1),
      })
    );
  };

  const maxAmountToRefund = (value: string): boolean | string => {
    const amountInCents = Number(removeAllDigits(value));

    return (
      amountInCents <= maximumAmountToRefund ||
      t(`${localeStringRoot}.refund_form.alerts.maximum_amount_required`, {
        value: formatPaywallCurrencyAmount(
          charge.currency,
          maximumAmountToRefund,
        ),
      })
    );
  };

  return {
    transformValueIntoCurrency,
    minAmountToRefund,
    maxAmountToRefund,
  };
};
